/**
 * ManagerDashboard.jsx
 * 
 * Manager's Morning Briefing - Everything a manager needs at a glance
 * 
 * Sections:
 * - Good Morning header with date/time
 * - Priority Actions (requires immediate attention)
 * - Today's Snapshot (who's in/out, coverage)
 * - This Week (upcoming absences, key dates)
 * - Team Health (Bradford alerts, stats)
 * - Reminders (training, reviews, documents)
 * - Quick Actions (navigate to common tasks)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import { useAuth } from './AuthContext';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Avatar,
  IconButton,
  LinearProgress,
  Divider,
  Badge,
  Tooltip,
  CircularProgress,
  Alert,
  Skeleton
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AccessTime as PendingIcon,
  Person as PersonIcon,
  Groups as TeamIcon,
  EventBusy as AbsentIcon,
  EventAvailable as PresentIcon,
  Warning as WarningIcon,
  School as TrainingIcon,
  RateReview as ReviewIcon,
  Description as DocumentIcon,
  ArrowForward as ArrowIcon,
  Refresh as RefreshIcon,
  WbSunny as MorningIcon,
  Schedule as ClockIcon,
  TrendingUp as TrendingIcon,
  CalendarMonth as CalendarIcon,
  Sick as SickIcon,
  BeachAccess as HolidayIcon,
  ChildCare as ParentalIcon,
  LocalHospital as MedicalIcon,
  Gavel as FlexibleIcon,
  NotificationsActive as AlertIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getLeaveTypeIcon = (type) => {
  const typeMap = {
    'annual_leave': <HolidayIcon fontSize="small" />,
    'sick_leave': <SickIcon fontSize="small" />,
    'sickness': <SickIcon fontSize="small" />,
    'maternity': <ParentalIcon fontSize="small" />,
    'paternity': <ParentalIcon fontSize="small" />,
    'medical': <MedicalIcon fontSize="small" />,
    'medical_appointment': <MedicalIcon fontSize="small" />,
  };
  return typeMap[type?.toLowerCase()] || <AbsentIcon fontSize="small" />;
};

const getLeaveTypeColor = (type) => {
  const colorMap = {
    'annual_leave': '#0d9488',
    'sick_leave': '#ef4444',
    'sickness': '#ef4444',
    'maternity': '#8b5cf6',
    'paternity': '#8b5cf6',
    'medical': '#f59e0b',
    'medical_appointment': '#f59e0b',
  };
  return colorMap[type?.toLowerCase()] || '#64748b';
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

// Priority Action Card
const PriorityActionCard = ({ icon, title, count, urgent, onClick, color }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      border: urgent ? '2px solid #ef4444' : '1px solid #e2e8f0',
      borderRadius: 2,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: urgent ? '#fef2f2' : 'white',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        borderColor: color || '#667eea'
      }
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: urgent ? '#fee2e2' : `${color}15` || '#f1f5f9',
              color: urgent ? '#ef4444' : color || '#64748b'
            }}
          >
            {icon}
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight="bold" color={urgent ? 'error.main' : 'text.primary'}>
              {count}
            </Typography>
          </Box>
        </Box>
        {urgent && (
          <Chip
            label="Action Required"
            size="small"
            color="error"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
        )}
      </Box>
    </CardContent>
  </Card>
);

// Employee Absence Row
const AbsenceRow = ({ employee, reason, startDate, endDate, status }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
      borderBottom: '1px solid #f1f5f9',
      '&:last-child': { borderBottom: 'none' }
    }}
  >
    <Box display="flex" alignItems="center" gap={2}>
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: '0.85rem',
          backgroundColor: getLeaveTypeColor(reason),
          color: 'white'
        }}
      >
        {getInitials(employee)}
      </Avatar>
      <Box>
        <Typography variant="body2" fontWeight="600">
          {employee}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          {getLeaveTypeIcon(reason)}
          <Typography variant="caption" color="text.secondary">
            {reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Typography>
        </Box>
      </Box>
    </Box>
    <Box textAlign="right">
      <Typography variant="caption" color="text.secondary">
        {new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        {endDate && startDate !== endDate && (
          <> - {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
        )}
      </Typography>
      {status && (
        <Chip
          label={status}
          size="small"
          sx={{
            ml: 1,
            height: 20,
            fontSize: '0.65rem',
            backgroundColor: status === 'Pending' ? '#fef3c7' : '#dcfce7',
            color: status === 'Pending' ? '#92400e' : '#166534'
          }}
        />
      )}
    </Box>
  </Box>
);

// Pending Request Card (for approvals)
const PendingRequestCard = ({ request, onApprove, onReject, type = 'holiday' }) => (
  <Card
    elevation={0}
    sx={{
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      mb: 1.5,
      '&:hover': { borderColor: '#667eea' }
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box display="flex" gap={2}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              backgroundColor: type === 'holiday' ? '#0d948815' : '#7c3aed15',
              color: type === 'holiday' ? '#0d9488' : '#7c3aed'
            }}
          >
            {type === 'holiday' ? <HolidayIcon /> : <FlexibleIcon />}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight="600">
              {request.employee_name || request.employeeName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {type === 'holiday' ? (
                <>
                  {new Date(request.start_date || request.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  {(request.end_date || request.endDate) && (request.start_date || request.startDate) !== (request.end_date || request.endDate) && (
                    <> - {new Date(request.end_date || request.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
                  )}
                  <Typography component="span" sx={{ mx: 1 }}>•</Typography>
                  {request.total_days || request.number_of_days_holiday || 1} day(s)
                </>
              ) : (
                request.flexibility_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
              )}
            </Typography>
            {request.reason && (
              <Chip
                label={request.reason.replace(/_/g, ' ')}
                size="small"
                sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        </Box>
        <Box display="flex" gap={0.5}>
          <Tooltip title="Approve">
            <IconButton
              size="small"
              onClick={() => onApprove(request.id)}
              sx={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                '&:hover': { backgroundColor: '#bbf7d0' }
              }}
            >
              <ApproveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton
              size="small"
              onClick={() => onReject(request.id)}
              sx={{
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                '&:hover': { backgroundColor: '#fecaca' }
              }}
            >
              <RejectIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Alert/Reminder Row
const ReminderRow = ({ icon, title, subtitle, count, color, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 1.5,
      px: 2,
      borderRadius: 1,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': { backgroundColor: '#f8fafc' }
    }}
  >
    <Box display="flex" alignItems="center" gap={2}>
      <Avatar
        sx={{
          width: 36,
          height: 36,
          backgroundColor: `${color}15`,
          color: color
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography variant="body2" fontWeight="600">
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Box>
    <Badge
      badgeContent={count}
      color={count > 0 ? 'error' : 'default'}
      sx={{
        '& .MuiBadge-badge': {
          fontWeight: 600
        }
      }}
    >
      <ArrowIcon fontSize="small" color="action" />
    </Badge>
  </Box>
);

// Bradford Factor Mini Widget
const BradfordMiniWidget = ({ data, onClick }) => {
  const hasAlerts = data?.alertCount > 0;
  
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: hasAlerts ? '2px solid #f59e0b' : '1px solid #e2e8f0',
        borderRadius: 2,
        cursor: 'pointer',
        backgroundColor: hasAlerts ? '#fffbeb' : 'white',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: hasAlerts ? '#fef3c7' : '#dcfce7',
              color: hasAlerts ? '#f59e0b' : '#22c55e'
            }}
          >
            <SpeedIcon />
          </Avatar>
          <Box flex={1}>
            <Typography variant="body2" fontWeight="600">
              Bradford Factor
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasAlerts ? `${data.alertCount} employees need attention` : 'All clear'}
            </Typography>
          </Box>
          {hasAlerts && (
            <Box display="flex" gap={0.5}>
              {data.critical > 0 && (
                <Chip label={`${data.critical} critical`} size="small" color="error" sx={{ fontSize: '0.65rem' }} />
              )}
              {data.red > 0 && (
                <Chip label={`${data.red} serious`} size="small" sx={{ fontSize: '0.65rem', backgroundColor: '#fee2e2', color: '#991b1b' }} />
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({
    pendingHolidays: [],
    pendingFlexible: [],
    todayAbsent: [],
    weekAbsences: [],
    teamStats: { total: 0, present: 0, absent: 0 },
    bradfordData: { alertCount: 0, critical: 0, red: 0, orange: 0 },
    trainingExpiring: 0,
    reviewsDue: 0,
    documentsExpiring: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Parallel API calls for performance
      const results = await Promise.allSettled([
        api.get('/holidays/pending'),
        api.get('/flexible-working/requests?status=submitted'),
        api.get('/dashboard/absences/today'),
        api.get('/dashboard/absences/week'),
        api.get('/employees/active-count'),
        api.get('/bradford/alerts'),
        api.get('/dashboard/training/expiring-soon'),
        api.get('/dashboard/reviews/due-soon'),
        api.get('/dashboard/documents/expiring-soon')
      ]);

      setData({
        pendingHolidays: results[0].status === 'fulfilled' ? (results[0].value.data.requests || results[0].value.data || []) : [],
        pendingFlexible: results[1].status === 'fulfilled' ? (results[1].value.data.requests || []) : [],
        todayAbsent: results[2].status === 'fulfilled' ? (results[2].value.data.absences || results[2].value.data || []) : [],
        weekAbsences: results[3].status === 'fulfilled' ? (results[3].value.data.absences || results[3].value.data || []) : [],
        teamStats: {
          total: results[4].status === 'fulfilled' ? (results[4].value.data.count || 0) : 0,
          present: 0,
          absent: results[2].status === 'fulfilled' ? (results[2].value.data.absences?.length || results[2].value.data?.length || 0) : 0
        },
        bradfordData: results[5].status === 'fulfilled' ? {
          alertCount: results[5].value.data.count || 0,
          critical: results[5].value.data.alerts?.filter(a => a.level === 'critical').length || 0,
          red: results[5].value.data.alerts?.filter(a => a.level === 'red').length || 0,
          orange: results[5].value.data.alerts?.filter(a => a.level === 'orange').length || 0
        } : { alertCount: 0, critical: 0, red: 0, orange: 0 },
        trainingExpiring: results[6].status === 'fulfilled' ? (results[6].value.data.count || results[6].value.data?.length || 0) : 0,
        reviewsDue: results[7].status === 'fulfilled' ? (results[7].value.data.count || results[7].value.data?.length || 0) : 0,
        documentsExpiring: results[8].status === 'fulfilled' ? (results[8].value.data.count || results[8].value.data?.length || 0) : 0
      });
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleApproveHoliday = async (id) => {
    try {
      await api.post(`/holidays/${id}/approve`);
      setData(prev => ({
        ...prev,
        pendingHolidays: prev.pendingHolidays.filter(h => h.id !== id)
      }));
    } catch (err) {
      console.error('Failed to approve holiday:', err);
    }
  };

  const handleRejectHoliday = async (id) => {
    try {
      await api.post(`/holidays/${id}/reject`);
      setData(prev => ({
        ...prev,
        pendingHolidays: prev.pendingHolidays.filter(h => h.id !== id)
      }));
    } catch (err) {
      console.error('Failed to reject holiday:', err);
    }
  };

  // Calculate stats
  const presentCount = data.teamStats.total - data.todayAbsent.length;
  const coveragePercent = data.teamStats.total > 0 ? Math.round((presentCount / data.teamStats.total) * 100) : 100;
  const totalPendingActions = data.pendingHolidays.length + data.pendingFlexible.length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          mb: 3,
          borderRadius: 0
        }}
      >
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <MorningIcon sx={{ fontSize: 28 }} />
                <Typography variant="h4" fontWeight="700">
                  {getGreeting()}, {user?.firstName || user?.name?.split(' ')[0] || 'Manager'}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {formatDate(new Date())}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={2}>
                <ClockIcon fontSize="small" sx={{ opacity: 0.8 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Last updated: {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Quick Stats Bar */}
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              mt: 3,
              pt: 3,
              borderTop: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Box>
              <Typography variant="h3" fontWeight="700">
                {presentCount}/{data.teamStats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Team Present Today
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
            <Box>
              <Typography variant="h3" fontWeight="700">
                {coveragePercent}%
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Coverage
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
            <Box>
              <Typography variant="h3" fontWeight="700" color={totalPendingActions > 0 ? '#fef08a' : 'inherit'}>
                {totalPendingActions}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Pending Actions
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {/* Priority Alert */}
        {totalPendingActions > 0 && (
          <Alert
            severity="warning"
            icon={<AlertIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/time')}>
                View All
              </Button>
            }
          >
            <Typography fontWeight="600">
              You have {totalPendingActions} pending approval{totalPendingActions !== 1 ? 's' : ''} requiring your attention
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* LEFT COLUMN - Priority Actions & Approvals */}
          <Grid item xs={12} lg={8}>
            {/* Priority Action Cards */}
            <Typography variant="h6" fontWeight="700" color="text.primary" gutterBottom>
              Priority Actions
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <PriorityActionCard
                  icon={<HolidayIcon />}
                  title="Holiday Requests"
                  count={data.pendingHolidays.length}
                  urgent={data.pendingHolidays.length > 0}
                  onClick={() => navigate('/time')}
                  color="#0d9488"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <PriorityActionCard
                  icon={<FlexibleIcon />}
                  title="Flexible Working"
                  count={data.pendingFlexible.length}
                  urgent={data.pendingFlexible.length > 0}
                  onClick={() => navigate('/flexible-working/manage')}
                  color="#7c3aed"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <PriorityActionCard
                  icon={<TrainingIcon />}
                  title="Training Expiring"
                  count={data.trainingExpiring}
                  urgent={data.trainingExpiring > 3}
                  onClick={() => navigate('/submit_training')}
                  color="#f59e0b"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <PriorityActionCard
                  icon={<ReviewIcon />}
                  title="Reviews Due"
                  count={data.reviewsDue}
                  urgent={data.reviewsDue > 0}
                  onClick={() => navigate('/employee_reviews')}
                  color="#3b82f6"
                />
              </Grid>
            </Grid>

            {/* Pending Approvals */}
            {(data.pendingHolidays.length > 0 || data.pendingFlexible.length > 0) && (
              <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="700">
                      Pending Approvals
                    </Typography>
                    <Chip
                      label={`${data.pendingHolidays.length + data.pendingFlexible.length} waiting`}
                      size="small"
                      color="warning"
                    />
                  </Box>

                  {/* Holiday Requests */}
                  {data.pendingHolidays.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Time Off Requests
                      </Typography>
                      {data.pendingHolidays.slice(0, 3).map((request) => (
                        <PendingRequestCard
                          key={request.id}
                          request={request}
                          onApprove={handleApproveHoliday}
                          onReject={handleRejectHoliday}
                          type="holiday"
                        />
                      ))}
                      {data.pendingHolidays.length > 3 && (
                        <Button
                          fullWidth
                          variant="text"
                          onClick={() => navigate('/time')}
                          sx={{ mt: 1 }}
                        >
                          View all {data.pendingHolidays.length} requests
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Flexible Working Requests */}
                  {data.pendingFlexible.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Flexible Working Requests
                      </Typography>
                      {data.pendingFlexible.slice(0, 2).map((request) => (
                        <PendingRequestCard
                          key={request.id}
                          request={request}
                          onApprove={() => navigate(`/flexible-working/manage/${request.id}`)}
                          onReject={() => navigate(`/flexible-working/manage/${request.id}`)}
                          type="flexible"
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Today's Absences */}
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="700">
                    Who's Off Today
                  </Typography>
                  <Chip
                    icon={<AbsentIcon sx={{ fontSize: 16 }} />}
                    label={`${data.todayAbsent.length} absent`}
                    size="small"
                    sx={{
                      backgroundColor: data.todayAbsent.length > 0 ? '#fee2e2' : '#dcfce7',
                      color: data.todayAbsent.length > 0 ? '#991b1b' : '#166534'
                    }}
                  />
                </Box>

                {data.todayAbsent.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      backgroundColor: '#f0fdf4',
                      borderRadius: 2
                    }}
                  >
                    <PresentIcon sx={{ fontSize: 48, color: '#22c55e', mb: 1 }} />
                    <Typography variant="body1" fontWeight="600" color="success.main">
                      Full team present today!
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {data.todayAbsent.map((absence, idx) => (
                      <AbsenceRow
                        key={idx}
                        employee={absence.employee_name || absence.employeeName || absence.name}
                        reason={absence.reason || absence.leave_type || 'Leave'}
                        startDate={absence.start_date || absence.startDate}
                        endDate={absence.end_date || absence.endDate}
                      />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* This Week */}
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="700">
                    This Week's Absences
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ArrowIcon />}
                    onClick={() => navigate('/holiday_calendar')}
                  >
                    View Calendar
                  </Button>
                </Box>

                {data.weekAbsences.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No absences scheduled this week
                  </Typography>
                ) : (
                  <Box>
                    {data.weekAbsences.slice(0, 5).map((absence, idx) => (
                      <AbsenceRow
                        key={idx}
                        employee={absence.employee_name || absence.employeeName || absence.name}
                        reason={absence.reason || absence.leave_type || 'Leave'}
                        startDate={absence.start_date || absence.startDate}
                        endDate={absence.end_date || absence.endDate}
                        status={absence.status}
                      />
                    ))}
                    {data.weekAbsences.length > 5 && (
                      <Button
                        fullWidth
                        variant="text"
                        onClick={() => navigate('/holiday_calendar')}
                        sx={{ mt: 1 }}
                      >
                        View all {data.weekAbsences.length} absences
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT COLUMN - Team Health & Quick Actions */}
          <Grid item xs={12} lg={4}>
            {/* Team Coverage */}
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  Team Coverage
                </Typography>
                <Box sx={{ position: 'relative', pt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={coveragePercent}
                    sx={{
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: '#fee2e2',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 6,
                        backgroundColor: coveragePercent >= 80 ? '#22c55e' : coveragePercent >= 60 ? '#f59e0b' : '#ef4444'
                      }
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      {presentCount} present
                    </Typography>
                    <Typography variant="caption" fontWeight="600">
                      {coveragePercent}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.todayAbsent.length} absent
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Bradford Factor */}
            <Box mb={3}>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Team Health
              </Typography>
              <BradfordMiniWidget
                data={data.bradfordData}
                onClick={() => navigate('/bradford-factor')}
              />
            </Box>

            {/* Reminders */}
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  Reminders
                </Typography>
                <Box>
                  <ReminderRow
                    icon={<TrainingIcon />}
                    title="Training Expiring"
                    subtitle="Certificates due for renewal"
                    count={data.trainingExpiring}
                    color="#f59e0b"
                    onClick={() => navigate('/submit_training')}
                  />
                  <ReminderRow
                    icon={<ReviewIcon />}
                    title="Reviews Due"
                    subtitle="Performance reviews scheduled"
                    count={data.reviewsDue}
                    color="#3b82f6"
                    onClick={() => navigate('/employee_reviews')}
                  />
                  <ReminderRow
                    icon={<DocumentIcon />}
                    title="Documents Expiring"
                    subtitle="Contracts & certifications"
                    count={data.documentsExpiring}
                    color="#8b5cf6"
                    onClick={() => navigate('/documents')}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  Quick Actions
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { label: 'Team', icon: <TeamIcon />, path: '/team', color: '#9C27B0' },
                    { label: 'Time Off', icon: <CalendarIcon />, path: '/time', color: '#00bcd4' },
                    { label: 'Training', icon: <TrainingIcon />, path: '/submit_training', color: '#f59e0b' },
                    { label: 'Reviews', icon: <ReviewIcon />, path: '/employee_reviews', color: '#4CAF50' },
                    { label: 'Documents', icon: <DocumentIcon />, path: '/documents', color: '#607D8B' },
                    { label: 'Reports', icon: <TrendingIcon />, path: '/reports', color: '#FF9800' }
                  ].map((action) => (
                    <Grid item xs={6} key={action.label}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={action.icon}
                        onClick={() => navigate(action.path)}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          py: 1.5,
                          borderColor: '#e2e8f0',
                          color: 'text.primary',
                          '&:hover': {
                            borderColor: action.color,
                            backgroundColor: `${action.color}08`
                          }
                        }}
                      >
                        {action.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ManagerDashboard;